import React, { useEffect, useState } from 'react';
import { Button, Group, Box, Select, NumberInput, TextInput, ActionIcon, Text, Divider, FileInput, SimpleGrid, ScrollArea, Stack } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconEye, IconPencil, IconTrash, IconUpload } from '@tabler/icons-react';
import HttpClient from '../helpers/HttpClient';
import { useNavigate, useLocation } from 'react-router-dom';
import { notify } from '../components/NotificationUtils';
import { Booking, BookingType } from '../types/BookingModel';
import { PreloadBooking } from './Booking';
import { Receipt } from '../types/receipt.type';
import { config } from '../helpers/Config';
import { getAccessToken } from '../helpers/TokenRefresh';
import axios from 'axios';
import dayjs from 'dayjs';

interface PendingBooking {
  localId: string;
  id?: number;
  bookingDate: Date;
  personId: string;
  personLabel: string;
  bookingTypeId: string;
  bookingTypeLabel: string;
  bookingTypeMultiplier: number;
  bookingText: string;
  bookingAmount: number;
}

const emptyForm = () => ({
  bookingDate: new Date(),
  personId: null as string | null,
  bookingTypeId: null as string | null,
  bookingText: '',
  bookingAmount: 0,
});

const BookingEdit: React.FC<{ id: number | undefined }> = ({ id }) => {
  const [loading, setLoading] = useState(true);
  const [persons, setPersons] = useState<{ value: string; label: string }[]>([]);
  const [bookingTypes, setBookingTypes] = useState<{ value: string; label: string; multiplier: number }[]>([]);
  const [pending, setPending] = useState<PendingBooking[]>([]);
  const [editingLocalId, setEditingLocalId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptDocumentDate, setReceiptDocumentDate] = useState<Date | null>(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const httpClient = new HttpClient<Booking>('/booking');

  useEffect(() => {
    const fetchPersons = () =>
      new HttpClient<any>('/person').getAll({ slim: 'true' }).then((list) =>
        setPersons(list.map((p: any) => ({ value: p.id.toString(), label: `${p.lastName} ${p.firstName}` })))
      );

    const fetchBookingTypes = () =>
      new HttpClient<BookingType>('/bookingType').getAll().then((list) =>
        setBookingTypes(list.map((t) => ({ value: t.id.toString(), label: t.title, multiplier: t.multiplier ?? 0 })))
      );

    const fetchBooking = async () => {
      if (!id) return;
      const result = await httpClient.getById(id);
      const b = result.data as any;
      setPending([{
        localId: crypto.randomUUID(),
        id: b.id,
        bookingDate: new Date(b.bookingDate),
        personId: b.person?.id?.toString() ?? '',
        personLabel: `${b.person?.lastName ?? ''} ${b.person?.firstName ?? ''}`,
        bookingTypeId: b.bookingType?.id?.toString() ?? '',
        bookingTypeLabel: b.bookingType?.title ?? '',
        bookingTypeMultiplier: b.bookingType?.multiplier ?? 0,
        bookingText: b.bookingText,
        bookingAmount: b.bookingAmount,
      }]);
    };

    const fetchReceipts = async () => {
      if (!id) return;
      const result = await new HttpClient<Receipt>(`/receipt/by-booking/${id}`).getAll();
      setReceipts(result);
    };

    Promise.all([fetchPersons(), fetchBookingTypes(), fetchBooking(), fetchReceipts()]).then(() => {
      const preload: PreloadBooking[] | undefined = (location.state as any)?.preload;
      if (preload?.length) {
        setPending(preload.map((p) => ({
          localId: crypto.randomUUID(),
          bookingDate: new Date(p.bookingDate),
          personId: p.personId,
          personLabel: p.personLabel,
          bookingTypeId: p.bookingTypeId,
          bookingTypeLabel: p.bookingTypeLabel,
          bookingTypeMultiplier: p.bookingTypeMultiplier,
          bookingText: p.bookingText,
          bookingAmount: p.bookingAmount,
        })));
      }
    }).finally(() => setLoading(false));
  }, []);

  const validate = () => {
    if (!form.bookingDate) { notify('Fehler', 'Buchungsdatum fehlt', 'red'); return false; }
    if (!form.personId) { notify('Fehler', 'Person fehlt', 'red'); return false; }
    if (!form.bookingTypeId) { notify('Fehler', 'Buchungsart fehlt', 'red'); return false; }
    if (!form.bookingText.trim()) { notify('Fehler', 'Text fehlt', 'red'); return false; }
    return true;
  };

  const buildPending = (): PendingBooking => {
    const bt = bookingTypes.find((t) => t.value === form.bookingTypeId)!;
    const p = persons.find((p) => p.value === form.personId)!;
    return {
      localId: editingLocalId ?? crypto.randomUUID(),
      id: editingLocalId ? pending.find((r) => r.localId === editingLocalId)?.id : undefined,
      bookingDate: form.bookingDate,
      personId: form.personId!,
      personLabel: p?.label ?? '',
      bookingTypeId: form.bookingTypeId!,
      bookingTypeLabel: bt?.label ?? '',
      bookingTypeMultiplier: bt?.multiplier ?? 0,
      bookingText: form.bookingText,
      bookingAmount: form.bookingAmount,
    };
  };

  const handleAdd = () => {
    if (!validate()) return;
    if (editingLocalId) {
      setPending((prev) => prev.map((r) => r.localId === editingLocalId ? buildPending() : r));
      setEditingLocalId(null);
    } else {
      setPending((prev) => [...prev, buildPending()]);
    }
    setForm(emptyForm());
  };

  const handleEdit = (row: PendingBooking) => {
    setEditingLocalId(row.localId);
    setForm({
      bookingDate: new Date(row.bookingDate),
      personId: row.personId,
      bookingTypeId: row.bookingTypeId,
      bookingText: row.bookingText,
      bookingAmount: row.bookingAmount,
    });
  };

  const handleRemove = (localId: string) => {
    setPending((prev) => prev.filter((r) => r.localId !== localId));
    if (editingLocalId === localId) {
      setEditingLocalId(null);
      setForm(emptyForm());
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      const res = await httpClient.delete(id);
      if (res.status !== 200) throw new Error();
      notify('Erfolg', `Buchung ${id} gelöscht`, 'green');
      navigate(-1);
    } catch {
      notify('Fehler', 'Löschen fehlgeschlagen', 'red');
    }
  };

  const handleSaveAll = async () => {
    if (pending.length === 0) { notify('Hinweis', 'Keine Buchungen vorhanden', 'yellow'); return; }
    setSaving(true);
    try {
      const payload = pending.map((r) => ({
        ...(r.id ? { id: r.id } : {}),
        bookingDate: r.bookingDate,
        bookingText: r.bookingText,
        bookingAmount: r.bookingAmount,
        person: { id: parseInt(r.personId) },
        bookingType: { id: r.bookingTypeId },
      }));
      const batchClient = new HttpClient<any>('/booking/batch');
      const res: any = await batchClient.post(payload as any);
      if (res.status !== 200) throw new Error();
      notify('Erfolg', `${pending.length} Buchung(en) gespeichert`, 'green');
      navigate(-1);
    } catch (e) {
      notify('Fehler', 'Speichern fehlgeschlagen: ' + e, 'red');
    } finally {
      setSaving(false);
    }
  };

  const handleReceiptUpload = async () => {
    if (!receiptFile || !id) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', receiptFile);
      const token = await getAccessToken();
      const params = new URLSearchParams({ bookingId: String(id) });
      if (receiptDocumentDate) {
        const d = receiptDocumentDate instanceof Date ? receiptDocumentDate : new Date(receiptDocumentDate as any);
        if (!isNaN(d.getTime())) params.set('documentDate', d.toISOString());
      }
      const res = await axios.post(
        `${config.apiUrl}/receipt?${params}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setReceipts(prev => [...prev, res.data.data]);
      setReceiptFile(null);
      setReceiptDocumentDate(null);
      notify('Erfolg', 'Beleg hochgeladen', 'green');
    } catch {
      notify('Fehler', 'Upload fehlgeschlagen', 'red');
    } finally {
      setUploading(false);
    }
  };

  const handleReceiptPreview = async (receipt: Receipt) => {
    const token = await getAccessToken();
    fetch(`${config.apiUrl}/receipt/${receipt.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const win = window.open(blobUrl, '_blank');
        win?.addEventListener('load', () => URL.revokeObjectURL(blobUrl), { once: true });
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      })
      .catch(() => notify('Fehler', 'Vorschau fehlgeschlagen', 'red'));
  };

  const handleReceiptDelete = async (receipt: Receipt) => {
    try {
      await new HttpClient<Receipt>('/receipt').delete(receipt.id);
      setReceipts(prev => prev.filter(r => r.id !== receipt.id));
      notify('Erfolg', 'Beleg gelöscht', 'green');
    } catch {
      notify('Fehler', 'Löschen fehlgeschlagen', 'red');
    }
  };

  if (loading) return <div>Laden...</div>;

  return (
    <Box>
      {/* Form */}
      <Box p="md" style={{ border: '1px solid #dee2e6', borderRadius: 8, marginBottom: 16 }}>
        <Text fw={600} mb="sm">{editingLocalId ? 'Buchung bearbeiten' : 'Neue Buchung'}</Text>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
          <DatePickerInput
            clearable
            valueFormat="DD.MM.YYYY"
            label="Datum"
            value={form.bookingDate}
            onChange={(v: string | null) => setForm((f) => ({ ...f, bookingDate: v ? new Date(v) : new Date() }))}
          />
          <Select
            label="Person"
            placeholder="Person wählen"
            searchable
            data={persons}
            value={form.personId}
            onChange={(v) => setForm((f) => ({ ...f, personId: v }))}
          />
          <Select
            label="Buchungsart"
            placeholder="Art wählen"
            data={bookingTypes}
            value={form.bookingTypeId}
            onChange={(v) => setForm((f) => ({ ...f, bookingTypeId: v }))}
          />
          <TextInput
            label="Text"
            value={form.bookingText}
            onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, bookingText: v })); }}
          />
          <NumberInput
            label="Betrag"
            value={form.bookingAmount}
            onChange={(v) => setForm((f) => ({ ...f, bookingAmount: Number(v) }))}
            decimalSeparator=","
          />
        </SimpleGrid>
        <Group mt="sm">
          <Button onClick={handleAdd}>
            {editingLocalId ? 'Aktualisieren' : 'Hinzufügen'}
          </Button>
          {editingLocalId && (
            <Button variant="subtle" onClick={() => { setEditingLocalId(null); setForm(emptyForm()); }}>
              Abbrechen
            </Button>
          )}
        </Group>
      </Box>

      {/* Pending list */}
      {pending.length > 0 && (
        <ScrollArea mb="md">
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>Datum</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Person</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Art</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Text</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Betrag</th>
                <th style={{ padding: '8px', width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {pending.map((row) => (
                <tr
                  key={row.localId}
                  style={{
                    background: row.localId === editingLocalId ? 'var(--mantine-color-yellow-0)' : undefined,
                    borderBottom: '1px solid #f1f3f5',
                  }}
                >
                  <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{dayjs(row.bookingDate).format('DD.MM.YYYY')}</td>
                  <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{row.personLabel}</td>
                  <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{row.bookingTypeLabel}</td>
                  <td style={{ padding: '8px' }}>{row.bookingText}</td>
                  <td style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {row.bookingAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                  </td>
                  <td style={{ padding: '8px' }}>
                    <Group gap={4} justify="center">
                      <ActionIcon variant="light" color="blue" onClick={() => handleEdit(row)}>
                        <IconPencil size={16} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="red" onClick={() => handleRemove(row.localId)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      )}

      <Stack gap="xs" mb="lg">
        <Group wrap="wrap">
          <Button onClick={handleSaveAll} loading={saving} disabled={pending.length === 0}>
            Alle speichern ({pending.length})
          </Button>
          {id && <Button color="red" variant="light" onClick={handleDelete}>Löschen</Button>}
          <Button variant="subtle" onClick={() => navigate(-1)}>Abbrechen</Button>
        </Group>
      </Stack>

      {/* Receipts section — only for existing bookings */}
      {id && (
        <>
          <Divider my="lg" label="Belege" labelPosition="left" />
          <SimpleGrid cols={{ base: 1, sm: 2 }} mb="sm">
            <FileInput
              label="Beleg hinzufügen"
              placeholder="PDF oder Bild wählen..."
              accept="application/pdf,image/*"
              value={receiptFile}
              onChange={setReceiptFile}
              leftSection={<IconUpload size={16} />}
            />
            <DatePickerInput
              clearable
              valueFormat="DD.MM.YYYY"
              label="Belegdatum (optional)"
              value={receiptDocumentDate}
              onChange={(v: any) => setReceiptDocumentDate(v ? (v instanceof Date ? v : new Date(v)) : null)}
            />
          </SimpleGrid>
          <Button onClick={handleReceiptUpload} loading={uploading} disabled={!receiptFile} mb="md" fullWidth={{ base: true, sm: false } as any}>
            Hochladen
          </Button>
          {receipts.length > 0 && (
            <ScrollArea>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 400 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Dateiname</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Belegdatum</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Hochgeladen</th>
                    <th style={{ padding: '8px', width: 80 }} />
                  </tr>
                </thead>
                <tbody>
                  {receipts.map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f1f3f5' }}>
                      <td style={{ padding: '8px' }}>{r.fileName}</td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{r.documentDate ? dayjs(r.documentDate).format('DD.MM.YYYY') : '—'}</td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{dayjs(r.createdAt).format('DD.MM.YYYY HH:mm')}</td>
                      <td style={{ padding: '8px' }}>
                        <Group gap={4} justify="center">
                          <ActionIcon variant="light" color="blue" onClick={() => handleReceiptPreview(r)}>
                            <IconEye size={16} />
                          </ActionIcon>
                          <ActionIcon variant="light" color="red" onClick={() => handleReceiptDelete(r)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          )}
        </>
      )}
    </Box>
  );
};

export default BookingEdit;
